import { NgFor, NgIf } from '@angular/common';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectModule as MatSelectModule,
} from '@angular/material/legacy-select';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  CuttingButt,
  CuttingButtFormService,
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
  PanelBase,
  PanelUtilsHelper,
  ValidWidthButt,
} from 'cloud-shared-lib';
import * as _ from 'lodash';

interface SwapingButt {
  oldButt: CuttingButt;
  newButt: CuttingButt;
  panelThickness: number;
}

export interface ButtReplacementCustomDialogData {
  panels: PanelBase[];
  matInMatBaseId: number;
  materialThickness: number;
  onlyInColor?: boolean;
  useAllIfInColorEmpty?: boolean;
}

@Component({
  selector: 'cutting-butt-replacement-custom-dialog',
  templateUrl: './butt-replacement-custom-dialog.component.html',
  styleUrls: ['./butt-replacement-custom-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatIconModule,
    MatToolbarModule,
    MatDividerModule,
    FormsModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ButtReplacementCustomDialogComponent implements OnInit {
  materialBaseDialogRef: MatDialogRef<MaterialBaseButtsDialogComponent>;
  dataSource: MatTableDataSource<SwapingButt> = new MatTableDataSource();
  displayedColumns = ['oldButt', 'newButt', 'panelThickness'];
  buttsInColorList: CuttingButt[] = [];
  panel: PanelBase[] = [];
  otherButts: CuttingButt[] = [];
  validWidthButts: ValidWidthButt[] = [];

  set panels(val: PanelBase[]) {
    this.panel = _.cloneDeep(val);

    const butts: SwapingButt[] = _(this.panel)
      .flatMap(p => {
        const panelThickness = PanelUtilsHelper.getFullPanelThickness(this.data.materialThickness, p);
        return _.compact([
          p.w1,
          p.w2,
          p.l1,
          p.l2,
          p.panelOutline.topLeft.butt,
          p.panelOutline.topRight.butt,
          p.panelOutline.bottomLeft.butt,
          p.panelOutline.bottomRight.butt,
        ]).map(butt => ({ oldButt: butt, newButt: butt, panelThickness: panelThickness }));
      })
      .uniqWith(_.isEqual)
      .value();

    this.dataSource.data = butts;
  }

  constructor(
    public dialog: MatDialog,
    private buttFormService: CuttingButtFormService,
    public dialogRef: MatDialogRef<ButtReplacementCustomDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ButtReplacementCustomDialogData
  ) {
    this.panels = data.panels;
    this.validWidthButts = PanelUtilsHelper.getUniqueValidWidthButts(this.data.materialThickness, this.panel);
  }

  ngOnInit(): void {
    this.buttFormService
      .getMatchedButtsForClient(this.data.matInMatBaseId, this.data.materialThickness)
      .then((response: CuttingButt[]) => {
        this.buttsInColorList = response;
      });
  }

  saveAndClose(): void {
    this.panel.forEach(p => {
      const panelThickness = PanelUtilsHelper.getFullPanelThickness(this.data.materialThickness, p);
      let swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.l1) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.l1 = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.l2) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.l2 = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.w1) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.w1 = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.w2) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.w2 = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.panelOutline.topLeft.butt) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.panelOutline.topLeft.butt = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb => sb.oldButt && _.isEqual(sb.oldButt, p.panelOutline.topRight.butt) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.panelOutline.topRight.butt = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb =>
          sb.oldButt && _.isEqual(sb.oldButt, p.panelOutline.bottomRight.butt) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.panelOutline.bottomRight.butt = swappingButt.newButt;
        swappingButt = undefined;
      }

      swappingButt = this.dataSource.data.find(
        sb =>
          sb.oldButt && _.isEqual(sb.oldButt, p.panelOutline.bottomLeft.butt) && sb.panelThickness === panelThickness
      );
      if (swappingButt) {
        p.panelOutline.bottomLeft.butt = swappingButt.newButt;
        swappingButt = undefined;
      }
    });
    this.dialogRef.close(this.panel);
  }

  openMaterialBaseDialog(swappingButt: SwapingButt): Promise<CuttingButt | undefined> {
    const validWidthButt = _.find(this.validWidthButts, (b: ValidWidthButt) => {
      return b.idButt === swappingButt.oldButt.id && b.panelThickness === swappingButt.panelThickness;
    });

    this.materialBaseDialogRef = this.dialog.open(MaterialBaseButtsDialogComponent, {
      panelClass: 'material-base-butt-list-dialog',
      disableClose: false,
      width: '1000px',
      data: {
        panelThickness: validWidthButt.panelThickness,
      } as MaterialBaseEdgeDialogData,
    });

    return new Promise((resolve, reject) => {
      this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingButt | undefined) => {
        this.materialBaseDialogRef = null;
        resolve(result);
      }, reject);
    });
  }

  useMatBase(): boolean {
    return !this.data.onlyInColor || (this.data.useAllIfInColorEmpty && !this.buttsInColorList?.length);
  }

  async setButtFromMatBase(swappingButt: SwapingButt, select: MatSelect): Promise<void> {
    const newButtFromBM = await this.openMaterialBaseDialog(swappingButt);

    if (newButtFromBM) {
      const nbindex = this.dataSource.data.findIndex(
        b => b.newButt && _.isEqual(b.newButt, swappingButt.newButt) && b.panelThickness === swappingButt.panelThickness
      );

      this.otherButts.push(newButtFromBM);
      this.dataSource.data[nbindex].newButt = newButtFromBM;
      select.valueChange.emit(newButtFromBM);
    }
  }
}
